/*
 * WF subscriptions — PDP frequency picker.
 *
 * Renders for products tagged `subscription` (mount: snippets/wf-subscription-frequency.liquid).
 * On frequency select it marks the product form so the resulting cart line is a
 * subscription:
 *   - hidden `properties[SUBSCRIPTION FREQUENCY]` = "{N} weeks" (numeric-first; the
 *     worker parses N from the first token, and the cart module also reads it).
 *   - form `quantity` = N  (prepaid pricing = variant price x N), native qty stepper hidden.
 *   - add-to-cart gated until a frequency is chosen.
 *   - plans larger than the selected variant's stock are disabled (data-variant-stock),
 *     reducing stock-short submissions. The cart caps N at qty if stock changes later.
 *
 * The N delivery dates + N personal messages are collected later in the cart by
 * assets/wf-subscription-cart.js. See docs/subscriptions.md §8.
 */
(function () {
  'use strict';

  var FREQ_PROP = 'SUBSCRIPTION FREQUENCY';
  var API_BASE = 'https://planners.dand3.com';

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (ch) {
      return ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      })[ch];
    });
  }

  function optionButtons(root) {
    return Array.prototype.slice.call(root.querySelectorAll('.wf-subscription__opt'));
  }

  function normalizeFrequencyOption(raw) {
    if (!raw || raw.enabled === false) return null;
    var deliveries = parseInt(raw.deliveries, 10);
    if (!(deliveries > 0)) {
      var match = String(raw.value || '').match(/(\d+)/);
      deliveries = match ? parseInt(match[1], 10) : 0;
    }
    if (!(deliveries > 0)) return null;
    return {
      deliveries: deliveries,
      label: String(raw.label || 'weekly deliveries').trim() || 'weekly deliveries'
    };
  }

  function renderFrequencyOptions(root, frequencies) {
    var optionsEl = root.querySelector('.wf-subscription__options');
    if (!optionsEl || !Array.isArray(frequencies) || !frequencies.length) return false;
    var seen = {};
    var normalized = frequencies
      .map(normalizeFrequencyOption)
      .filter(function (frequency) {
        if (!frequency || seen[frequency.deliveries]) return false;
        seen[frequency.deliveries] = true;
        return true;
      });
    if (!normalized.length) return false;

    optionsEl.innerHTML = normalized.map(function (frequency) {
      return [
        '<button type="button" class="wf-subscription__opt" role="radio" aria-checked="false" data-weeks="',
        escapeHtml(frequency.deliveries),
        '">',
        '<span class="wf-subscription__opt-n">',
        escapeHtml(frequency.deliveries),
        '</span>',
        '<span class="wf-subscription__opt-unit">',
        escapeHtml(frequency.label),
        '</span>',
        '</button>'
      ].join('');
    }).join('');
    return true;
  }

  function loadFrequencyConfig(root) {
    var storeKey = root.getAttribute('data-store-key') || 'WF';
    var url = API_BASE + '/api/subscriptions/public?storeKey=' + encodeURIComponent(storeKey);
    return fetch(url, {
      method: 'GET',
      credentials: 'omit',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Subscription config HTTP ' + response.status);
        return response.json();
      })
      .then(function (payload) {
        return payload && payload.config && Array.isArray(payload.config.frequencies)
          ? payload.config.frequencies
          : [];
      })
      .catch(function (err) {
        console.warn('[wf-subscription] using fallback frequencies', err);
        return [];
      });
  }

  function init(root) {
    if (!root || root.__wfSubBound) return;
    root.__wfSubBound = true;

    var formId = root.getAttribute('data-form-id') || '';
    var sectionId = root.getAttribute('data-section-id') || '';
    var mainForm = formId ? document.getElementById(formId) : null;
    var stickyForm = sectionId ? document.getElementById('product-form-sticky-' + sectionId) : null;
    var forms = [mainForm, stickyForm].filter(Boolean);
    if (!forms.length) return;

    var optionsEl = root.querySelector('.wf-subscription__options');
    var gateHint = root.querySelector('[data-wf-sub-gate-hint]');
    var stockHint = root.querySelector('[data-wf-sub-stock-hint]');
    var selected = null;
    var variantStock = {};
    try {
      variantStock = JSON.parse(root.getAttribute('data-variant-stock') || '{}') || {};
    } catch (err) {
      console.warn('[wf-subscription] stock map unavailable; cart quantity cap remains active', err);
    }

    // ---- form helpers -----------------------------------------------------
    function upsertHidden(form, name, value) {
      var sel = 'input[name="' + name.replace(/"/g, '\\"') + '"]';
      var el = form.querySelector(sel);
      if (!el) {
        el = document.createElement('input');
        el.type = 'hidden';
        el.name = name;
        form.appendChild(el);
      }
      el.value = value;
      return el;
    }

    function setQuantity(form, n) {
      // Prefer the visible qty input (Alpine x-model="qty"); fall back to a hidden field.
      var qty = form.querySelector('input[name="quantity"]');
      if (qty) {
        qty.value = String(n);
        qty.dispatchEvent(new Event('input', { bubbles: true }));
        qty.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        upsertHidden(form, 'quantity', String(n));
      }
      hideQtyStepper(form);
    }

    function hideQtyStepper(form) {
      // Hide ONLY the dedicated quantity stepper so qty stays pinned to N. We must NOT
      // climb to the closest [x-data] from the input — that resolves to the buy-buttons
      // container (x-block-buy-buttons) and would hide the Add-to-Cart button too.
      var stepper = form.querySelector('[x-data*="xQuantitySelector"]');
      if (stepper) stepper.style.display = 'none';
    }

    function applyFrequency(n) {
      selected = n;
      forms.forEach(function (form) {
        upsertHidden(form, 'properties[' + FREQ_PROP + ']', n + ' weeks');
        upsertHidden(form, 'properties[_wf_sub_quantity_pricing]', 'v1');
        setQuantity(form, n);
      });
      optionButtons(root).forEach(function (b) {
        var on = b.getAttribute('data-weeks') === String(n);
        b.classList.toggle('wf-subscription__opt--on', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      setGated(false);
    }

    // ---- stock limits -----------------------------------------------------
    // Sellable qty for the currently selected variant; null = no limit.
    function stockLimit() {
      var idInput = forms[0].querySelector('input[name="id"]');
      var id = idInput ? String(idInput.value) : '';
      var limit = Object.prototype.hasOwnProperty.call(variantStock, id) ? variantStock[id] : null;
      return typeof limit === 'number' ? limit : null;
    }

    function applyStockLimits() {
      var limit = stockLimit();
      var anyBlocked = false;
      optionButtons(root).forEach(function (b) {
        var blocked = limit != null && parseInt(b.getAttribute('data-weeks'), 10) > limit;
        if (blocked) anyBlocked = true;
        b.disabled = blocked;
        b.classList.toggle('wf-subscription__opt--oos', blocked);
      });
      if (stockHint) {
        stockHint.hidden = !anyBlocked || !(limit > 0);
        if (limit > 0) {
          stockHint.textContent = 'Only ' + limit + (limit === 1 ? ' delivery' : ' deliveries') +
            ' left in stock. Longer plans are unavailable right now.';
        }
      }
      if (selected != null && limit != null && selected > limit) {
        selected = null;
        optionButtons(root).forEach(function (b) {
          b.classList.remove('wf-subscription__opt--on');
          b.setAttribute('aria-checked', 'false');
        });
        forms.forEach(function (form) {
          upsertHidden(form, 'properties[' + FREQ_PROP + ']', '');
          setQuantity(form, 1);
        });
      }
      // Reassert our gate without unlocking a button disabled by another validator.
      if (selected == null) setGated(true);
    }

    // ---- gating -----------------------------------------------------------
    function addButtons() {
      var list = [];
      forms.forEach(function (form) {
        var b = form.querySelector('button[name="add"], [type="submit"][name="add"]');
        if (b) list.push(b);
      });
      return list;
    }

    function setGated(gated) {
      addButtons().forEach(function (b) {
        if (gated) b.setAttribute('disabled', 'disabled');
        else if (b.getAttribute('data-available') !== 'false') b.removeAttribute('disabled');
      });
      if (gateHint) gateHint.hidden = !gated;
    }

    // Block the gate hint flashing until the user interacts.
    function flashGate() {
      if (gateHint) gateHint.hidden = false;
    }

    if (optionsEl) {
      optionsEl.addEventListener('click', function (event) {
        var target = event.target && event.target.closest ? event.target.closest('.wf-subscription__opt') : null;
        if (!target || !optionsEl.contains(target)) return;
        if (target.disabled) return;
        var n = parseInt(target.getAttribute('data-weeks'), 10);
        if (n > 0) applyFrequency(n);
      });
    }

    // Initial state: gated until a frequency is picked.
    setGated(true);
    applyStockLimits();
    // Native variant changes update the hidden id first, then render buttons.
    // Recheck after that render, and coalesce id changes from other pickers.
    var stockTimer = null;
    document.addEventListener('change', function (event) {
      if (!root.isConnected) return;
      if (forms.indexOf(event.target.form) < 0 || event.target.name !== 'id') return;
      clearTimeout(stockTimer);
      stockTimer = setTimeout(applyStockLimits, 0);
    });
    document.addEventListener('eurus:product-page-variant-select:updated:' + sectionId, function () {
      if (root.isConnected) applyStockLimits();
    });
    // Native variant rendering can re-enable Add to Cart. Validate again before
    // its click handler or any direct form submission can read stale fields.
    function guardPurchase(event) {
      applyStockLimits();
      if (selected != null) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      flashGate();
    }
    forms.forEach(function (form) {
      form.addEventListener('submit', guardPurchase, true);
      form.addEventListener('click', function (event) {
        var button = event.target.closest && event.target.closest('[name="add"]');
        if (button) guardPurchase(event);
      }, true);
    });
    // If a disabled add button is clicked, reveal the hint.
    addButtons().forEach(function (b) {
      (b.parentElement || b).addEventListener('click', function () {
        if (selected == null) flashGate();
      }, true);
    });

    loadFrequencyConfig(root).then(function (frequencies) {
      if (!renderFrequencyOptions(root, frequencies)) return;
      applyStockLimits();
      if (selected != null) {
        var hasSelected = optionButtons(root).some(function (button) {
          return button.getAttribute('data-weeks') === String(selected);
        });
        var limit = stockLimit();
        if (hasSelected && !(limit != null && selected > limit)) {
          applyFrequency(selected);
          return;
        }
      }
      selected = null;
      setGated(true);
    });
  }

  function boot() {
    document.querySelectorAll('[data-wf-subscription]').forEach(init);
  }

  // Re-hydrate when the quick-view popup injects its product HTML (innerHTML, so the
  // mount appears after boot and an inline <script> never runs). init() guards via
  // root.__wfSubBound, so re-running boot() is safe.
  function watchQuickView() {
    if (typeof MutationObserver === 'undefined') return;
    function attach(el) { new MutationObserver(boot).observe(el, { childList: true }); }
    var qv = document.getElementById('quickview-product-content');
    if (qv) { attach(qv); return; }
    var bodyObs = new MutationObserver(function () {
      var el = document.getElementById('quickview-product-content');
      if (el) { bodyObs.disconnect(); attach(el); }
    });
    bodyObs.observe(document.documentElement, { childList: true, subtree: true });
  }

  function start() { boot(); watchQuickView(); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
